import type { AcceptLanguage } from '~/const/locale';
import type { AnswerType } from '~/model/AnswerType';
import type {
  AnswerChoicesRankingScaleType,
  AnswerChoicesRankingType,
  AnswerUserChoicesType,
  AnswerUserType,
  FormatedAnswerUser,
} from '~/model/AnswerUserType';
import type { ConclusionType } from '~/model/ConclusionType';
import type { ContributionType } from '~/model/ContributionType';
import type { CriteriaChoiceType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import type { UriType } from '~/model/GlobalTypes';
import type {
  InteractionType,
  UserInteractionStatusType,
} from '~/model/InteractionType';
import type { SectionType } from '~/model/SectionType';
import type { SportType } from '~/model/SportType';
import type { SelectedTimeSlotType } from '~/model/TimeSlotType';
import { scroller } from 'react-scroll';
import { getTranslationForUserLanguage } from '~/components/utils';
import {
  api,
  apiTypes,
  statusContribution,
} from '~/const/api';
import { isNotEmptyString } from '../utils';

const {
  TEXT,
  INTEGER,
  SELECT,
  VAS1,
  VAS2,
  SCALE,
  RADIO,
  CHOICES_JAR,
  CONDITIONAL,
  CHECK,
  PICTURE,
  FILE,
  RANKING,
  RANKING_PICTURE,
  RANKING_SCALE,
  STAR_RATING,
  GRID,
} = apiTypes;

export const scrollToQuestion = (questionId) => {
  scroller.scrollTo(`question-${questionId}`, {
    delay: 100,
    duration: 500,
    offset: -100,
    smooth: true,
  });
};

interface FormatAnswerUserProps {
  type: string;
  answerUser: AnswerUserType;
  questionId: string;
  subQuestionId: string;
  formatAnswerValue: any;
  value: any;
  other_answer: boolean;
  media?: string;
}

export const formatAnswerUser = ({
  type,
  answerUser,
  questionId,
  subQuestionId,
  formatAnswerValue,
  value,
  other_answer,
  media,
}: FormatAnswerUserProps): AnswerUserType => {
  switch (type) {
    case STAR_RATING:
    case GRID:
      return {
        ...answerUser,
        [questionId]: {
          ...answerUser[questionId],
          answer_choices: {
            ...Object.fromEntries(
              Object.entries(
                answerUser[questionId]?.answer_choices || {},
              ).filter(
                ([_key, answerChoice]) =>
                  typeof answerChoice === 'number'
                  || (Array.isArray(answerChoice) && answerChoice.length > 0),
              ),
            ),
            [subQuestionId]: formatAnswerValue,
          },
          type,
        },
      };
    case CHECK:
      if (answerUser[questionId]) {
        return {
          ...answerUser,
          [questionId]: {
            ...(answerUser[questionId]?.answer_choices && {
              answer_choices: answerUser[questionId].answer_choices,
            }),
            ...(answerUser[questionId]?.value_text && {
              value_text: answerUser[questionId].value_text,
            }),
            ...(!other_answer && { answer_choices: value }),
            ...(other_answer && { value_text: value }),
            type,
          },
        };
      } else {
        return {
          ...answerUser,
          [questionId]: {
            ...(!other_answer && { answer_choices: value }),
            ...(other_answer && { value_text: value }),
            type,
          },
        };
      }
    case RADIO:
    case CONDITIONAL:
    case CHOICES_JAR:
      return {
        ...answerUser,
        [questionId]: {
          answer_choices: !other_answer ? formatAnswerValue : [],
          ...(type === RADIO
            && other_answer && {
            value_text: other_answer ? value : null,
          }),
          type,
        },
      };
    case SELECT:
    case RANKING:
    case RANKING_SCALE:
    case RANKING_PICTURE:
      if (!Object.keys(formatAnswerValue).length)
        return {};
      return {
        ...answerUser,
        [questionId]: {
          answer_choices: formatAnswerValue,
          type,
        },
      };
    case FILE:
    case PICTURE:
      return {
        ...answerUser,
        [questionId]: media && {
          type,
          value: formatAnswerValue,
          media,
        },
      };
    default:
      return {
        ...answerUser,
        [questionId]: {
          type,
          value: formatAnswerValue,
        },
      };
  }
};

export const shouldAnswerBeDeleted = (
  answer_choices: AnswerUserChoicesType,
  id: UriType,
  previousAnswers?: AnswerType[],
) => {
  if (!(previousAnswers || []).some(answer => answer.question === id))
    return false;

  switch (answer_choices.type) {
    case TEXT:
      return !answer_choices.value || !(answer_choices.value as string).trim();
    case INTEGER:
      return answer_choices.value === '' || answer_choices.value === null;
    case SELECT:
      return !(answer_choices.answer_choices as string[])?.length;
    case CHECK:
      return (
        !(answer_choices?.answer_choices as string[])?.length
        && !answer_choices?.value_text?.trim()
      );
    case FILE:
    case PICTURE:
      return !answer_choices.media;
    default:
      return false;
  }
};

export const formatAnswerUserPostData = (
  answer_choices: AnswerUserChoicesType,
  questionUri: string,
  contribution: ContributionType,
  previousAnswers?: AnswerType[],
): FormatedAnswerUser[] => {
  const subQuestionAnswers
    = previousAnswers?.filter(a => a.question === questionUri) || [];

  const answerId = subQuestionAnswers[0]?.id;
  const toDelete = shouldAnswerBeDeleted(
    answer_choices,
    questionUri,
    previousAnswers,
  );

  let dataObject = {};

  switch (answer_choices.type) {
    case TEXT:
      dataObject = { value_text: answer_choices.value };
      break;
    case INTEGER:
      dataObject = { value_int: answer_choices.value };
      break;
    case SELECT:
      dataObject = { choices: answer_choices.answer_choices };
      break;
    case VAS1:
    case VAS2:
    case SCALE:
      dataObject = { value_float: answer_choices.value };
      break;
    case RADIO:
      dataObject = {
        choices: answer_choices.answer_choices,
        value_text: answer_choices.value_text || null,
      };
      break;
    case CHOICES_JAR:
      if (answer_choices.answer_choices) {
        dataObject = {
          choices: answer_choices.answer_choices,
        };
      }
      break;
    case CONDITIONAL:
      dataObject = { choices: answer_choices.answer_choices };
      break;
    case CHECK:
      dataObject = {
        choices: answer_choices.answer_choices,
        ...(answer_choices.value_text && {
          value_text: answer_choices.value_text,
        }),
      };
      break;
    case PICTURE:
    case FILE:
      dataObject = { media: answer_choices.value };
      break;
    case RANKING:
    case RANKING_PICTURE:
      dataObject = {
        choices: (answer_choices.answer_choices as AnswerChoicesRankingType)
          .choices,
        ranking: (answer_choices.answer_choices as AnswerChoicesRankingType)
          .ranking,
      };
      break;
    case RANKING_SCALE:
      dataObject = {
        choices: (
          answer_choices.answer_choices as AnswerChoicesRankingScaleType
        ).choices,
        ranking_scale: (
          answer_choices.answer_choices as AnswerChoicesRankingScaleType
        ).ranking_scale,
      };
      break;
    default:
      dataObject = { choices: answer_choices };
      break;
  }

  if ([STAR_RATING, GRID].includes(answer_choices.type)) {
    const subQuestionKeys = Object.keys(answer_choices.answer_choices);

    return (
      subQuestionKeys?.length > 0
      && subQuestionKeys
        .filter((sub_question) => {
          const choices = answer_choices.answer_choices[sub_question];
          return (
            typeof choices === 'number'
            || (Array.isArray(choices) && choices.length > 0)
          );
        })
        .map((sub_question) => {
          const choices = answer_choices.answer_choices[sub_question];
          const subQuestionAnswer = subQuestionAnswers.find(
            a => a.sub_question && a.sub_question === sub_question,
          );
          return {
            answerId: subQuestionAnswer?.id,
            contribution:
              contribution.id && `/api/${api.CONTRIBUTIONS}/${contribution.id}`,
            delete: toDelete,
            question: questionUri,
            sub_question,
            ...(answer_choices.type === STAR_RATING
              ? {
                  value_float: choices,
                }
              : { choices }),
          };
        })
    );
  }

  // create an object Data for the answer
  return [
    {
      answerId,
      contribution:
        contribution.id && `/api/${api.CONTRIBUTIONS}/${contribution.id}`,
      question: questionUri,
      ...dataObject,
      delete: toDelete,
    },
  ];
};

export const formatTimeSlotPostData = (
  selectedTimeSlots: string[],
  contribution: ContributionType,
  previousSelectedTimeSlots: SelectedTimeSlotType[],
) => {
  const timeSlotPostData = [];
  // Get Previous chosen timeslots URI Array
  const previousSelectedTimeSlotsUri
    = previousSelectedTimeSlots
      && previousSelectedTimeSlots.length > 0
      && previousSelectedTimeSlots.map(t => t.time_slot && t.time_slot['@id']);

  // Get previous chosen timeslot not selected anymore
  // in order to delete them
  const timeslotsToDelete
    = previousSelectedTimeSlotsUri
      && previousSelectedTimeSlotsUri.length > 0
      && previousSelectedTimeSlotsUri.filter((timeslot) => {
        return !selectedTimeSlots.includes(timeslot);
      });

  // Get new chosen timeslots to add
  // (not already present among the previous ones)
  const timeSlotsToAdd
    = previousSelectedTimeSlotsUri && previousSelectedTimeSlotsUri.length > 0
      ? selectedTimeSlots.filter((timeslot) => {
          return !previousSelectedTimeSlotsUri.includes(timeslot);
        })
      : selectedTimeSlots;

  // delete every unchosen timeslot
  if (timeslotsToDelete && timeslotsToDelete.length > 0) {
    for (const timeslot of timeslotsToDelete) {
      // store updated timeslot in the the requests Array
      let selectedTimeslotId;
      previousSelectedTimeSlots.forEach((previousSelectedTimeSlot) => {
        if (
          previousSelectedTimeSlot.time_slot
          && previousSelectedTimeSlot.time_slot['@id']
          && previousSelectedTimeSlot.time_slot['@id'] === timeslot
        ) {
          selectedTimeslotId = previousSelectedTimeSlot.id;
        }
      });
      const timeSlotData = {
        contribution:
          contribution.id && `/api/${api.CONTRIBUTIONS}/${contribution.id}`,
        delete: true,
        timeSlotId: selectedTimeslotId,
      };

      timeSlotPostData.push(timeSlotData);
    }
  }
  if (
    timeSlotsToAdd
    && timeSlotsToAdd.length > 0
    && !selectedTimeSlots.includes('none')
  ) {
    for (const time_slot of timeSlotsToAdd) {
      const timeSlotData = {
        contribution:
          contribution.id && `/api/${api.CONTRIBUTIONS}/${contribution.id}`,
        selectedTimeSlot: time_slot,
      };
      timeSlotPostData.push(timeSlotData);
    }
  }

  return timeSlotPostData;
};

export const isSectionEmpty = (section: SectionType | ConclusionType) => {
  return (
    section
    && (section.title === '' || section.title === null)
    && (section.body === ''
      || section.body === null
      || section.body === '<p><br /></p>')
    && section?.media === null
  );
};

export const searchProgressBarStep = (
  currentSectionIndex: number,
  sections: UriType[],
  currentQuestionIndex = 0,
  questions: UriType[] = [],
) => {
  const nbSections = sections?.length || 0;
  const nbQuestions = questions?.length;
  const getPercent = (index: number, total: number) =>
    Math.ceil((index / total) * 100);
  const sectionPercent
    = nbSections > 0 ? getPercent(currentSectionIndex, nbSections) : 0;
  const questionProgression
    = nbQuestions > 0 ? getPercent(currentQuestionIndex, nbQuestions) : 0;
  const questionsPercent
    = questionProgression > 0 ? questionProgression / nbSections : 0;
  return sectionPercent + questionsPercent;
};

export const getInitialSectionIndex = (
  interaction: InteractionType,
  userInteractionStatus: UserInteractionStatusType,
  userContribution: ContributionType,
): number => {
  const sectionIndex
    = interaction.sections.length > 1
      && userInteractionStatus?.current_user_contributor
      && Object.keys(userContribution).length !== 0
      && userContribution.status !== statusContribution.submitted
      ? interaction.sections.findIndex(
          s => s === userContribution.current_section,
        )
      : 0;
  return sectionIndex;
};

export const userInformationCompleted = (
  atHomeInteraction: boolean,
  phone: string,
  city: string,
  zipcode: string,
  address: string,
): boolean => {
  return (
    (atHomeInteraction
      && isNotEmptyString(phone)
      && isNotEmptyString(city)
      && isNotEmptyString(zipcode)
      && isNotEmptyString(address))
    || (!atHomeInteraction && isNotEmptyString(phone))
  );
};

export const userInformationModified = (
  userInformation: any,
  atHomeInteraction: boolean,
  phone: string,
  city: string,
  zipcode: string,
  address: string,
): boolean => {
  return (
    (atHomeInteraction
      && (phone !== userInformation.phone
        || city !== userInformation.city
        || zipcode !== userInformation.zipcode
        || address !== userInformation.address))
      || (!atHomeInteraction && phone !== userInformation.phone)
  );
};

export const setContactParameter = (
  key: string,
  value: string,
  updateState: (value: string) => void,
): void => {
  switch (key) {
    case 'phone':
      updateState(value);
      break;
    case 'city':
      updateState(value);
      break;
    case 'zipcode':
      updateState(value);
      break;
    case 'address':
      updateState(value);
      break;
    default:
      break;
  }
};

export const retrieveTranslatedLabel = (
  entity: CriteriaChoiceType | CriteriaType | SportType,
  userLanguage: AcceptLanguage,
): string => {
  return entity?.translations
    ? getTranslationForUserLanguage(entity?.translations, userLanguage, 'fr_FR')
      ?.label
    : entity?.label;
};
